<template>
  <AdminLayout>
    <Head title="Company Locations" />

    <template #sectionTitle> Settings </template>

    <div class="headline flex flex-row justify-between mx-4 sm:mx-12">
      <h2 class="text-2xl font-semibold text-blue-600 pb-5 pt-5">
        <SimpleHeading :title="location.data.name"></SimpleHeading>
      </h2>

      <div class="relative inline-flex shadow-sm rounded-md self-start mt-2">
        <button
          @click.prevent="saveLocation()"
          :disabled="form.processing"
          type="button"
          class="inline-flex items-center px-4 py-3 mr-3 border border-transparent text-sm font-medium rounded-md shadow-sm text-gray-100 bg-blue-600 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-600"
        >
          Save Changes
        </button>

        <Menu
          as="div"
          class="relative inline-block text-left"
        >
          <div>
            <MenuButton
              class="inline-flex justify-center w-full rounded-md border border-gray-400 shadow-sm px-4 py-3 bg-gray-100 text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-400 focus:ring-indigo-500"
            >
              Actions
              <ChevronDownIcon
                class="-mr-1 ml-2 h-5 w-5"
                aria-hidden="true"
              />
            </MenuButton>
          </div>

          <transition
            enter-active-class="transition ease-out duration-100"
            enter-from-class="opacity-0 scale-95"
            enter-to-class="opacity-100 scale-100"
            leave-active-class="transition ease-in duration-75"
            leave-from-class="opacity-100 scale-100"
            leave-to-class="opacity-0 scale-95"
          >
            <MenuItems
              class="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-gray-100 ring-1 ring-gray-400 ring-opacity-5 divide-y divide-gray-400 focus:outline-none z-50"
            >
              <div class="py-1">
                <MenuItem v-slot="{ active }">
                  <a
                    href="#"
                    @click.prevent="toggleAddRegion(true)"
                    :class="[
                      'text-gray-700 hover:text-gray-200 hover:bg-blue-600 group flex items-center px-4 py-2 text-sm'
                    ]"
                  >
                    <DuplicateIcon
                      class="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-200"
                      aria-hidden="true"
                    />
                    Add Region
                  </a>
                </MenuItem>
              </div>
              <div class="py-1">
                <MenuItem v-slot="{ active }">
                  <a
                    href="#"
                    @click.prevent="toggleAddUser(true)"
                    :class="[
                      'text-gray-700 hover:text-gray-200 hover:bg-blue-600 group flex items-center px-4 py-2 text-sm'
                    ]"
                  >
                    <UserAddIcon
                      class="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-200"
                      aria-hidden="true"
                    />
                    Add User
                  </a>
                </MenuItem>
              </div>
              <div class="py-1">
                <MenuItem v-slot="{ active }">
                  <a
                    @click.prevent="toggleDelete(true)"
                    :disabled="deleteForm.processing"
                    :class="[
                      'text-gray-700 hover:text-gray-200 hover:bg-blue-600 group flex items-center px-4 py-2 text-sm'
                    ]"
                  >
                    <TrashIcon
                      class="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-200"
                      aria-hidden="true"
                    />
                    Delete
                  </a>
                </MenuItem>
              </div>
            </MenuItems>
          </transition>
        </Menu>
      </div>
    </div>

    <div class="tabs flex-1 flex flex-col">
      <PageTabGroup>
        <PageTabList>
          <PageTab label="Details" />
          <PageTab label="Users" />
          <PageTab label="Delivery" />
          <PageTab label="Pickup" />
        </PageTabList>

        <PageTabPanels class="form bg-gray-100 flex flex-col flex-1">
          <PageTabPanel>
            <div
              v-if="$page.props.flash.success"
              class="alert alert-success"
            >
              <p class="alert-text">
                {{ $page.props.flash.success }}
              </p>
            </div>

            <form
              method="post"
              @submit.prevent="saveLocation()"
            >
              <div class="grid grid-cols-12 gap-6">
                <div class="input-group col-span-6 sm:col-span-3 lg:col-span-4">
                  <label for="name"> Name </label>
                  <input
                    type="text"
                    v-model="form.name"
                    name="name"
                    id="name"
                    autocomplete="given-name"
                  />
                  <p
                    v-if="getErrors('name', form)"
                    class="input-error"
                  >
                    {{ getErrors('name', form) }}
                  </p>
                </div>

                <div class="input-group col-span-6 sm:col-span-3 lg:col-span-4">
                  <MultiSelectInput
                    v-model="form.types"
                    :options="locationTypes"
                    placeholder="Please select a type..."
                  >
                    Types
                  </MultiSelectInput>
                  <p
                    v-if="getErrors('types', form)"
                    class="input-error"
                  >
                    {{ getErrors('types', form) }}
                  </p>
                </div>

                <div class="input-group col-span-6 sm:col-span-3 lg:col-span-4">
                  <label for="state"> Timezone </label>
                  <select
                    id="state"
                    v-model="form.timezone"
                    name="timezone"
                  >
                    <option :value="null">Select a timezone</option>
                    <option
                      v-for="(timezone, key) in timezones"
                      :value="timezone"
                    >
                      {{ timezone }}
                    </option>
                  </select>
                  <p
                    v-if="getErrors('timezone', form)"
                    class="input-error"
                  >
                    {{ getErrors('timezone', form) }}
                  </p>
                </div>

                <div class="input-group col-span-12 sm:col-span-4">
                  <label for="address_1"> License Number </label>
                  <input
                    type="text"
                    v-model="form.license_number"
                    name="license_number"
                    id="license_number"
                  />
                  <p
                    v-if="getErrors('license_number', form)"
                    class="input-error"
                  >
                    {{ getErrors('license_number', form) }}
                  </p>
                </div>

                <div class="input-group col-span-12 sm:col-span-4">
                  <label for="address_1"> Address 1 </label>
                  <input
                    type="text"
                    v-model="form.address.street_1"
                    name="address[address_1]"
                    id="address_1"
                    autocomplete="address_1"
                  />
                  <p
                    v-if="getErrors('address.street_1', form)"
                    class="input-error"
                  >
                    {{ getErrors('address.street_1', form) }}
                  </p>
                </div>

                <div class="input-group col-span-12 sm:col-span-4">
                  <label for="address_2"> Address 2 </label>
                  <input
                    type="text"
                    v-model="form.address.street_2"
                    name="address[address_2]"
                    id="address_2"
                    autocomplete="address_2"
                  />
                  <p
                    v-if="getErrors('address.street_2', form)"
                    class="input-error"
                  >
                    {{ getErrors('address.street_2', form) }}
                  </p>
                </div>

                <div class="input-group col-span-6 sm:col-span-6 lg:col-span-4">
                  <label for="city"> City </label>
                  <input
                    type="text"
                    v-model="form.address.city"
                    name="address[city]"
                    id="city"
                  />
                  <p
                    v-if="getErrors('address.city', form)"
                    class="input-error"
                  >
                    {{ getErrors('address.city', form) }}
                  </p>
                </div>

                <div class="input-group col-span-6 sm:col-span-3 lg:col-span-4">
                  <label for="state"> State </label>
                  <select
                    id="state"
                    v-model="form.address.state"
                    name="address[state]"
                  >
                    <option :value="null">Select a state</option>
                    <option
                      v-for="(state, key) in states"
                      :value="key"
                    >
                      {{ state }}
                    </option>
                  </select>
                  <p
                    v-if="getErrors('address.state', form)"
                    class="input-error"
                  >
                    {{ getErrors('address.state', form) }}
                  </p>
                </div>

                <div class="input-group col-span-6 sm:col-span-3 lg:col-span-4">
                  <label for="zip"> Zip </label>
                  <input
                    type="text"
                    v-model="form.address.zip"
                    name="address[zip]"
                    id="zip"
                  />
                  <p
                    v-if="getErrors('address.zip', form)"
                    class="input-error"
                  >
                    {{ getErrors('address.zip', form) }}
                  </p>
                </div>

                <div class="input-group col-span-6 sm:col-span-6 lg:col-span-4">
                  <label for="phone"> Phone </label>
                  <input
                    type="text"
                    v-model="form.phone"
                    name="phone"
                    id="phone"
                  />
                  <p
                    v-if="getErrors('phone', form)"
                    class="input-error"
                  >
                    {{ getErrors('phone', form) }}
                  </p>
                </div>

                <div class="input-group col-span-6 sm:col-span-3 lg:col-span-4">
                  <label for="email"> Email </label>
                  <input
                    type="text"
                    v-model="form.email"
                    name="email"
                    id="email"
                  />
                  <p
                    v-if="getErrors('email', form)"
                    class="input-error"
                  >
                    {{ getErrors('email', form) }}
                  </p>
                </div>

                <div class="input-group col-span-6 sm:col-span-3 lg:col-span-4">
                  <label for="website_url"> Website </label>
                  <input
                    type="text"
                    v-model="form.website_url"
                    name="website_url"
                    id="website_url"
                  />
                  <p
                    v-if="getErrors('website_url', form)"
                    class="input-error"
                  >
                    {{ getErrors('website_url', form) }}
                  </p>
                </div>

                <div class="input-group col-span-6 sm:col-span-3 lg:col-span-4">
                  <label for="dynamics_location_name"> Location UID </label>
                  <input
                    type="text"
                    v-model="form.settings.dynamics_location_name"
                    name="dynamics_location_name"
                    id="dynamics_location_name"
                  />
                  <p
                    v-if="getErrors('settings.dynamics_location_name', form)"
                    class="input-error"
                  >
                    {{ getErrors('settings.dynamics_location_name', form) }}
                  </p>
                </div>
              </div>
            </form>
          </PageTabPanel>

          <PageTabPanel>
            <div class="flex flex-col">
              <div class="relative -my-2 sm:-mx-6 lg:-mx-8">
                <div class="py-2 align-middle inline-block min-w-full">
                  <div class="shadow overflow-hidden border-b border-gray-400 sm:rounded-lg">
                    <table class="min-w-full divide-y divide-gray-400">
                      <thead class="bg-gray-400">
                        <tr>
                          <th
                            scope="col"
                            class="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider"
                          >
                            Role
                          </th>
                          <th
                            scope="col"
                            class="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider"
                          >
                            First Name
                          </th>
                          <th
                            scope="col"
                            class="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider"
                          >
                            Last Name
                          </th>
                          <th
                            scope="col"
                            class="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider"
                          >
                            Phone
                          </th>
                          <th
                            scope="col"
                            class="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider"
                          >
                            Email
                          </th>
                          <th
                            scope="col"
                            class="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider"
                          >
                            Created At
                          </th>
                          <th
                            scope="col"
                            class="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider"
                          >
                            Updated At
                          </th>
                          <th
                            scope="col"
                            class="relative px-6 py-3"
                          >
                            <span class="sr-only">Edit</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <!-- Odd row -->
                        <tr
                          v-for="user in users"
                          :key="user.email"
                        >
                          <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {{ user.company_role }}
                          </td>
                          <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {{ user.first_name }}
                          </td>
                          <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {{ user.last_name }}
                          </td>
                          <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {{ user.phone }}
                          </td>
                          <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {{ user.email }}
                          </td>
                          <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {{ formatDate(user.created_at) }}
                          </td>
                          <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {{ formatDate(user.updated_at) }}
                          </td>
                          <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <Popover>
                              <PopoverButton class="btn btn-secondary-outline">
                                <DotsHorizontalIcon
                                  class="h-4 w-4"
                                  aria-hidden="true"
                                />
                              </PopoverButton>

                              <PopoverPanel class="absolute z-50 p-2 right-6">
                                <div class="menu w-32 text-left">
                                  <div class="py-1">
                                    <Link
                                      :href="route('company.users.edit', { user: user.id })"
                                      class="menu-item menu-link"
                                    >
                                      Edit User
                                    </Link>
                                    <a
                                      href="#"
                                      class="menu-item menu-link"
                                      @click.prevent="toggleRemoveUser(user)"
                                    >
                                      Remove
                                    </a>
                                  </div>
                                </div>
                              </PopoverPanel>
                            </Popover>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </PageTabPanel>

          <PageTabPanel>
            <div class="flex flex-col">
              <div class="relative -my-2 sm:-mx-6 lg:-mx-8">
                <div class="py-2 align-middle inline-block min-w-full">
                  <div class="shadow overflow-hidden border-b border-gray-400 sm:rounded-lg">
                    <table class="min-w-full divide-y divide-gray-400">
                      <thead class="bg-gray-400">
                        <tr>
                          <th
                            scope="col"
                            class="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider"
                          >
                            Name
                          </th>
                          <th
                            scope="col"
                            class="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider"
                          >
                            Remaining Deliveries
                          </th>
                          <th
                            scope="col"
                            class="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider"
                          >
                            Location
                          </th>
                          <th
                            scope="col"
                            class="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider"
                          >
                            Creation Date
                          </th>
                          <th
                            scope="col"
                            class="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider"
                          >
                            Updated Date
                          </th>
                          <th
                            scope="col"
                            class="relative px-6 py-3"
                          >
                            <span class="sr-only">Edit</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="region in regions"
                          :key="region.id"
                        >
                          <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {{ region.name }}
                          </td>
                          <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {{ region.current_remaining_deliveries ?? 'NA' }}
                          </td>
                          <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {{ region.address }}
                          </td>
                          <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {{ formatDate(region.created_at) }}
                          </td>
                          <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {{ formatDate(region.updated_at) }}
                          </td>
                          <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <Popover>
                              <PopoverButton class="btn btn-secondary-outline">
                                <DotsHorizontalIcon
                                  class="h-4 w-4"
                                  aria-hidden="true"
                                />
                              </PopoverButton>

                              <PopoverPanel class="absolute z-50 p-2 right-6">
                                <div class="menu w-52 text-left">
                                  <div class="py-1">
                                    <a
                                      @click.prevent="openEditRegion(region)"
                                      href="#"
                                      class="menu-item menu-link"
                                    >
                                      Edit Region
                                    </a>
                                    <a
                                      @click.prevent="downloadSubRegions(region)"
                                      href="#"
                                      class="menu-item menu-link"
                                    >
                                      Download Zip Codes
                                    </a>
                                    <a
                                      @click.prevent="toggleDeleteRegion(region)"
                                      href="#"
                                      class="menu-item menu-link"
                                    >
                                      Remove
                                    </a>
                                  </div>
                                </div>
                              </PopoverPanel>
                            </Popover>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </PageTabPanel>

          <PageTabPanel>
            <div class="flex flex-col">
              <div class="grid grid-cols-2">
                <p
                  v-if="getErrors('schedule', form)"
                  class="input-error"
                >
                  {{ getErrors('schedule', form) }}
                </p>
                <PickupSchedule
                  :schedule="form.schedule"
                  @update:schedule="scheduleUpdated"
                />
              </div>
            </div>
          </PageTabPanel>
        </PageTabPanels>
      </PageTabGroup>
    </div>

    <template #modals>
      <SmallModal
        :is-open="showingAddUser"
        @close="showingAddUser = false"
      >
        <template #header> Add User </template>

        <div class="mt-4">
          <div class="input-group">
            <label for="add_user_id"> Select User </label>
            <select
              v-model="addUserForm.user_id"
              name="add_user_id"
            >
              <option :value="null">Select a user</option>
              <option
                v-for="(user, key) in availableUsers"
                :value="user.id"
              >
                {{ user.email }}
              </option>
            </select>
            <p
              v-if="getErrors('user_id', addUserForm)"
              class="input-error"
            >
              {{ getErrors('user_id', addUserForm) }}
            </p>
          </div>
        </div>

        <template #footer>
          <button
            type="button"
            class="inline-flex items-center px-4 py-2 mr-3 border border-transparent text-sm font-medium rounded-md shadow-sm text-gray-100 bg-blue-600 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-600"
            @click="addUser()"
          >
            Add User
          </button>
          <button
            type="button"
            class="btn btn-secondary ml-3"
            @click="showingAddUser = false"
          >
            Cancel
          </button>
        </template>
      </SmallModal>

      <SmallModal
        :is-open="showingRemoveUser"
        @close="showingRemoveUser = false"
      >
        <template #header> Remove User </template>

        <div class="mt-4">
          <div
            v-if="userToRemove"
            class="input-group"
          >
            <label> You are about to remove the following user: </label>
            <p class="mt-2 font-bold">
              {{ userToRemove.email }}
            </p>
          </div>
        </div>

        <template #footer>
          <button
            type="button"
            class="btn btn-danger"
            @click="removeUser()"
          >
            Remove
          </button>
          <button
            type="button"
            class="btn btn-secondary ml-3"
            @click="showingRemoveUser = false"
          >
            Cancel
          </button>
        </template>
      </SmallModal>

      <SmallModal
        :is-open="showingDeletePrompt"
        @close="showingDeletePrompt = false"
      >
        <template #header> Deleting Location </template>

        <div class="mt-4">
          <div class="input-group">
            <p>You are about to delete this location. Are you sure?</p>
          </div>
        </div>

        <template #footer>
          <button
            type="button"
            class="btn btn-danger"
            @click="deleteLocation()"
          >
            Yes, Delete
          </button>
          <button
            type="button"
            class="btn btn-secondary ml-3"
            @click="showingDeletePrompt = false"
          >
            Cancel
          </button>
        </template>
      </SmallModal>

      <SmallModal
        :is-open="showingAddRegion"
        @close="showingAddRegion = false"
      >
        <template #header> Create New Region </template>

        <div class="mt-4">
          <div class="input-group">
            <label for="add_region_name"> Name </label>
            <input
              type="text"
              v-model="addRegionForm.name"
              name="name"
              id="add_region_name"
            />
            <p
              v-if="getErrors('name', addRegionForm)"
              class="input-error"
            >
              {{ getErrors('name', addRegionForm) }}
            </p>
          </div>
          <div class="input-group mt-4">
            <label for="add_region_address"> Address </label>
            <input
              type="text"
              v-model="addRegionForm.address"
              name="address"
              id="add_region_address"
            />
            <p
              v-if="getErrors('address', addRegionForm)"
              class="input-error"
            >
              {{ getErrors('address', addRegionForm) }}
            </p>
          </div>
        </div>

        <template #footer>
          <button
            type="button"
            class="btn btn-primary"
            @click="addRegion()"
          >
            Add Region
          </button>
          <button
            type="button"
            class="btn btn-secondary ml-3"
            @click="showingAddRegion = false"
          >
            Cancel
          </button>
        </template>
      </SmallModal>

      <SmallModal
        :is-open="showingDeleteRegion"
        @close="regionToDelete = null"
      >
        <template #header> Remove Region </template>

        <div class="mt-4">
          <div
            v-if="regionToDelete"
            class="input-group"
          >
            <label> You are about to remove the following region: </label>
            <p class="mt-2 font-bold">
              {{ regionToDelete.name }}
            </p>
          </div>
        </div>

        <template #footer>
          <button
            type="button"
            class="btn btn-danger"
            @click="deleteRegion()"
          >
            Remove
          </button>
          <button
            type="button"
            class="btn btn-secondary ml-3"
            @click="regionToDelete = null"
          >
            Cancel
          </button>
        </template>
      </SmallModal>
    </template>

    <template #overlays>
      <OverlaySlideOver
        v-model:value="showingEditRegion"
        @close="showingEditRegion = false"
      >
        <template #header>
          <h3 class="text-lg leading-6 font-medium text-gray-900">Edit Region</h3>
          <p class="mt-1 max-w-2xl text-sm text-gray-600">Location and Schedule Details</p>
        </template>

        <EditRegion
          :region="selectedRegion"
          @update:region="regionSaved"
        />
      </OverlaySlideOver>
    </template>
  </AdminLayout>
</template>

<script setup>
import { PageTabGroup, PageTabList, PageTab, PageTabPanels, PageTabPanel } from '@/Components/PageTabs'
</script>

<script>
import AdminLayout from '@/Layouts/AdminLayout.vue'
import { Head, Link, useForm } from '@inertiajs/vue3'
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue'
import { DotsHorizontalIcon } from '@heroicons/vue/solid'
import checksErrors from '@/Mixins/checksErrors'
import dateFilters from '@/Mixins/dateFilters'
import MultiSelectInput from '@/Components/Forms/MultiSelectInput.vue'
import SimpleHeading from '@/Components/Headings/SimpleHeading'
import SmallModal from '@/Components/Modals/SmallModal.vue'
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'
import {
  ArchiveIcon,
  ArrowCircleRightIcon,
  ChevronDownIcon,
  DuplicateIcon,
  HeartIcon,
  PencilAltIcon,
  TrashIcon,
  UserAddIcon
} from '@heroicons/vue/solid'
import OverlaySlideOver from '@/Components/SlideOvers/OverlaySlideOver'
import EditRegion from '@/Partials/Regions/EditRegion'
import PickupSchedule from '@/Partials/Locations/PickupSchedule'

export default {
  mixins: [checksErrors, dateFilters],

  components: {
    AdminLayout,
    Head,
    Link,
    MultiSelectInput,
    SimpleHeading,
    SmallModal,
    Popover,
    PopoverButton,
    PopoverPanel,
    DotsHorizontalIcon,
    ArchiveIcon,
    ArrowCircleRightIcon,
    ChevronDownIcon,
    DuplicateIcon,
    HeartIcon,
    PencilAltIcon,
    TrashIcon,
    UserAddIcon,
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    OverlaySlideOver,
    EditRegion,
    PickupSchedule
  },

  props: {
    location: Object,
    companyUsers: Object,
    timezones: Array
  },

  data() {
    return {
      form: {
        name: '',
        types: [],
        phone: null,
        email: null,
        website_url: null,
        license_number: null,
        timezone: null,
        address: {
          address_1: null,
          address_2: null,
          city: null,
          state: null,
          zip: null
        },
        schedule: [],
        settings: {}
      },
      showingDeletePrompt: false,
      deleteForm: {},
      // users
      addUserForm: {
        user_id: null
      },
      showingAddUser: false,
      showingRemoveUser: false,
      userToRemove: null,
      removeUserForm: {},
      // regions
      showingAddRegion: false,
      addRegionForm: {
        name: '',
        address: ''
      },
      selectedRegion: null,
      showingEditRegion: false,
      updateRegionForm: {
        location_id: null,
        name: '',
        address: '',
        addresses: []
      },
      regionToDelete: null,
      deleteRegionForm: {},
      pickupRegion: [
        {
          day_of_week: 0,
          to: '19:00',
          from: '09:00',
          order_by_time: '16:20',
          delivery_fee: null,
          min_amount: 100,
          max_deliveries: null,
          has_delivery: false,
          is_active: true
        },
        {
          day_of_week: 1,
          to: '19:00',
          from: '09:00',
          order_by_time: '16:20',
          delivery_fee: 15,
          min_amount: 100,
          max_deliveries: null,
          has_delivery: true,
          is_active: true
        },
        {
          day_of_week: 2,
          to: '19:00',
          from: '09:00',
          order_by_time: '16:20',
          delivery_fee: null,
          min_amount: 100,
          max_deliveries: null,
          has_delivery: false,
          is_active: true
        },
        {
          day_of_week: 3,
          to: '19:00',
          from: '09:00',
          order_by_time: '16:20',
          delivery_fee: 15,
          min_amount: 100,
          max_deliveries: null,
          has_delivery: true,
          is_active: true
        },
        {
          day_of_week: 4,
          to: '19:00',
          from: '09:00',
          order_by_time: '16:20',
          delivery_fee: null,
          min_amount: 100,
          max_deliveries: null,
          has_delivery: false,
          is_active: true
        },
        {
          day_of_week: 5,
          to: '19:00',
          from: '09:00',
          order_by_time: '16:20',
          delivery_fee: null,
          min_amount: 100,
          max_deliveries: null,
          has_delivery: false,
          is_active: true
        },
        {
          day_of_week: 6,
          to: '19:00',
          from: '09:00',
          order_by_time: '16:20',
          delivery_fee: null,
          min_amount: 100,
          max_deliveries: null,
          has_delivery: false,
          is_active: true
        }
      ]
    }
  },

  created() {
    this.form = useForm({
      ...this.form,
      ...this.location.data
    })
    this.deleteForm = useForm(this.deleteForm)

    this.addUserForm = useForm(this.addUserForm)
    this.removeUserForm = useForm(this.removeUserForm)

    this.addRegionForm = useForm(this.addRegionForm)
    this.updateRegionForm = useForm(this.updateRegionForm)
    this.deleteRegionForm = useForm(this.deleteRegionForm)
  },

  computed: {
    users() {
      return this.location.data.users
    },
    regions() {
      return this.location.data.regions
    },
    availableUsers() {
      return this.companyUsers.data.filter((user) => {
        return (
          this.users.find((existingUser) => {
            return existingUser.id === user.id
          }) === undefined
        )
      })
    },
    states() {
      return this.$page.props.util.states
    },
    locationTypes() {
      return _.map(this.$page.props.util.location_types, (type) => {
        return {
          value: type,
          label: type
        }
      })
    },
    showingDeleteRegion() {
      return Boolean(this.regionToDelete)
    }
  },

  methods: {
    saveLocation() {
      this.form.put(this.route('company.locations.update', { location: this.location.data.id }), {
        onFinish: () => {}
      })
    },
    toggleDelete(to) {
      this.showingDeletePrompt = to
    },
    deleteLocation() {
      this.deleteForm.delete(this.route('company.locations.destroy', { location: this.location.data.id }), {
        onFinish: () => {}
      })
    },
    toggleAddUser(to) {
      this.showingAddUser = to
    },
    addUser() {
      this.addUserForm.post(this.route('locations.users.store', { location: this.location.data.id }), {
        onFinish: () => {
          this.addUserForm.reset()
          this.toggleAddUser(false)
        }
      })
    },
    toggleRemoveUser(user = null) {
      this.userToRemove = user
      this.showingRemoveUser = Boolean(user)
    },
    removeUser() {
      if (!this.userToRemove) {
        return
      }

      this.removeUserForm.delete(
        this.route('locations.users.destroy', { location: this.location.data.id, user: this.userToRemove.id }),
        {
          onFinish: () => {
            this.toggleRemoveUser(null)
          }
        }
      )
    },
    toggleAddRegion(to) {
      this.showingAddRegion = to
    },
    addRegion() {
      this.addRegionForm.post(this.route('locations.regions.store', { location: this.location.data.id }), {
        onFinish: () => {
          this.showingAddRegion = false
          this.addRegionForm.reset()
        }
      })
    },
    selectRegion(region) {
      this.selectedRegion = region
      this.updateRegionForm.reset()
      this.updateRegionForm = useForm({
        ...Object.assign({}, this.updateRegionForm),
        ...{
          location_id: region.location_id,
          name: region.name,
          address: region.address,
          addresses: region.addresses
        }
      })
    },
    updateRegion() {
      this.updateRegionForm.put(this.route('regions.update', { region: this.selectedRegion.id }), {
        onFinish: () => {
          this.updateRegionForm.reset()
        }
      })
    },
    toggleDeleteRegion(region = null) {
      this.regionToDelete = region
    },
    deleteRegion() {
      if (!this.regionToDelete) {
        return
      }

      this.deleteRegionForm.delete(this.route('regions.destroy', { region: this.regionToDelete.id }), {
        onFinish: () => {
          this.regionToDelete = null
        }
      })
    },
    openEditRegion(region) {
      this.selectRegion(region)
      this.showingEditRegion = true
    },
    downloadSubRegions(region) {
      window.open(route('regions.download', { region: region.id }, '_blank'))
    },
    regionSaved(event) {
      console.log('regionSaved()', event)
    },
    scheduleUpdated(schedule) {
      this.form.schedule = schedule
    }
  }
}
</script>
